import { useEffect, useState, type ReactNode } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Plus, ChevronUp, ChevronDown, ChevronRight, X, PlayCircle, Square, Lock } from 'lucide-react';
import { api, parseApiError, COMP_STAGE_TYPES, type CompStage, type CompStageType, type StandingsType } from '../lib/api';
import { suggestGroupPlan } from '../lib/groupPlan';
import { swissEndRiskMessage } from '../lib/pipelineView';
import { Card } from './Card';
import { StatusBadge } from './StatusBadge';
import { cn } from '../lib/cn';
import { SeriesFormatEditor } from './SeriesFormatEditor';
import { GameModesEditor } from './GameModesEditor';
import { SeedingTablePanel } from './SeedingTablePanel';
import { SeedingRoundsPanel } from './SeedingRoundsPanel';
import { DivisionsScoreboardPanel } from './DivisionsScoreboardPanel';
import { SwissRoundsPanel } from './SwissRoundsPanel';
import { EliminationBracketPanel } from './EliminationBracketPanel';
import { HelpText } from './HelpText';
import { displayClanName } from '../lib/displayName';
import { previewRound1 } from '../lib/bracketPreview';
import type { ModeConfig, SeriesFormat } from '../lib/modes';
import { epochToLocalDateTimeInputValue } from './MatchRosterInitPanel';

// ─────────────────────────────────────────────────────────────────────────────
// StagePipelinePanel — composable competition stage pipeline builder (T0 +
// per-stage config editors).
//
// Proves the ordered-list/lifecycle/lock model end to end (append, reorder,
// remove, start, end) AND gives every stage type its real settings, drawn
// from what the engines actually consume (src/shared/swiss.ts SwissConfig,
// src/shared/groupStage.ts GroupPlan): SEEDING's `rounds`, SWISS's
// winThreshold/lossThreshold, DIVISIONS's numGroups/advancePerGroup (+ a
// Suggest button wired to groupStage's suggestGroupPlan). ELIMINATION_SINGLE/
// ELIMINATION_DOUBLE/ROUND_ROBIN have no real engine knobs at all
// (generateBracket takes only entrants+format; round-robin scoring comes
// from the comp's game modes) — those cards show honest copy instead of a
// form. Progressive lock (backend-enforced on PATCH, shared/compStages.ts's
// canEditConfig) means config is only editable while a stage is PENDING;
// StageConfigFields below is shared verbatim with CompetitionCreateForm's
// StageComposer (create-time, local-only state, no lock — the comp doesn't
// exist yet).
//
// Per the product spec: the section itself only materializes once there's a
// reason to show it — an existing pipeline, or the admin actively adding the
// first stage. An empty comp with no pipeline and no in-flight "+ Stage"
// click shows nothing but a quiet entry point, not permanent empty-state
// clutter on every competition.
// ─────────────────────────────────────────────────────────────────────────────

export const STAGE_TYPE_LABELS: Record<CompStageType, string> = {
  SEEDING: 'Seeding',
  ROUND_ROBIN: 'Round Robin',
  DIVISIONS: 'Divisions',
  SWISS: 'Swiss',
  ELIMINATION_SINGLE: 'Single Elimination',
  ELIMINATION_DOUBLE: 'Double Elimination',
};

// T3 (per-stage config reshape): every stage type now carries an editable
// config — the type-specific knobs below for SEEDING/SWISS/DIVISIONS, PLUS a
// per-stage SERIES FORMAT block for EVERY type (including ELIM*/ROUND_ROBIN,
// whose bracket/scoring engine itself has no knobs but whose SERIES still
// does) — so Save/Cancel + the progressive-lock "locked" note now apply
// uniformly to every stage type, not just the three with real engine knobs.

const NO_CONFIG_COPY: Partial<Record<CompStageType, string>> = {
  ELIMINATION_SINGLE: 'Propose the bracket seed order from the teams entering this stage, review/reorder, then confirm to generate — no bracket configuration needed beyond the series format below.',
  ELIMINATION_DOUBLE: 'Propose the bracket seed order from the teams entering this stage, review/reorder, then confirm to generate — no bracket configuration needed beyond the series format below.',
  ROUND_ROBIN: "Matches are created from enrolled teams and scored using the competition's game modes — no extra configuration beyond the series format below.",
};

// T4: per-stage standings type — mirrors shared/compStages.ts's
// STANDINGS_TYPE_ALLOWED exactly (server is authoritative; this copy exists
// purely so the card knows whether to show the select at all).
// ELIMINATION_SINGLE/ELIMINATION_DOUBLE are excluded — a bracket stage is
// scored by progression through the bracket, never a win/loss or score
// points table, so there's nothing for a standings-type select to mean there.
export const STANDINGS_TYPE_STAGE_TYPES: readonly CompStageType[] = [
  'SEEDING', 'ROUND_ROBIN', 'SWISS', 'DIVISIONS',
];

// Group micro-label — same idiom used across the codebase for uppercase
// section tags (ClanBrandingHero/CaptainsPanel/etc): font-display, 10px,
// black weight, wide tracking, muted ink-meta. Every setting-group inside an
// expanded stage card carries one (Lisa's QA: "everything melts into each
// other") — space-y-6 between groups + a hairline divider (border-white/5,
// not a full border-color) draws the boundary without resorting to a box
// border. Module-scope (not just inside StageConfigFields) so
// StagePipelineCard's own planned-schedule group (P2-c, below) can reuse the
// exact same label treatment.
const GROUP_LABEL = 'mb-1.5 font-display text-[10px] font-black uppercase tracking-wider text-ink-meta';

// T5 (comp-lifecycle-pipeline, elimination gate): both bracket stage types
// share the SAME two-phase propose/confirm shape (mirroring DIVISIONS above)
// — this little helper is just so every branch below reads as "an
// elimination stage" rather than repeating the `type === 'A' || type === 'B'`
// check inline everywhere.
function isElimStage(type: CompStageType): boolean {
  return type === 'ELIMINATION_SINGLE' || type === 'ELIMINATION_DOUBLE';
}

// ── StageConfigFields — the per-type settings form ──────────────────────────
// Pure/presentational: no hooks of its own, no network calls. (The Game
// modes override below used to nest a CollapsibleSection with its own
// open/closed hook; it's now a single always-visible row — see the T5/
// UX-polish note further down — so there's no child-hook carve-out to track
// any more, just plainly zero hooks in this component.) Shared verbatim
// between StagePipelinePanel (an existing, persisted stage — wrapped in
// Save/Cancel + the progressive-lock
// `disabled` prop by StagePipelineCard below) and CompetitionCreateForm's
// StageComposer (local-only pre-create state, changes apply instantly, no
// save step). `numTeams` is the enrolled-clan count for the DIVISIONS
// Suggest button — undefined hides it (StageComposer has no teams yet at
// create time) rather than disabling it, per the product spec's muted
// "suggestion available once teams enroll" note. `modes` is the comp's
// game-mode DEFINITIONS (defined once, comp-level) — the TEMPLATE every new
// stage starts from and the fallback a stage with no `config.modes` of its
// own keeps inheriting; `defaultSeriesFormat` is the series a stage with no
// `config.seriesFormat` of its own falls back to display-only (the
// comp-level Ruleset/series — see CompetitionCreateForm.tsx) until the admin
// actually touches it, at which point it's written into this stage's own
// config explicitly.
//
// T5 (modes-per-stage): `config.modes`, when present, is this stage's OWN
// override of the comp-level mode definitions — an opt-in via "Edit for
// this stage" that, in ONE click, seeds a copy of the comp's CURRENT modes
// AND reveals the editor (no separate expand-then-override step);
// "Revert to competition modes" drops the key entirely, back to inheriting.
// The per-stage SERIES editor's mode-order <select> options must reflect
// whichever set is actually live for this stage — `effectiveModes` below
// feeds SeriesFormatEditor the STAGE's own modes when overridden, else the
// comp's — closing the gap where the series editor could otherwise offer
// modes the stage doesn't actually carry (shared/compStages.ts's
// modes<->seriesFormat.modeOrder cross-check is the server-side backstop for
// the same gap).
export function StageConfigFields({
  type, config, onChange, disabled, numTeams, modes, defaultSeriesFormat,
}: {
  type: CompStageType;
  config: Record<string, unknown>;
  onChange: (config: Record<string, unknown>) => void;
  disabled?: boolean;
  numTeams?: number;
  modes: ModeConfig[];
  defaultSeriesFormat: SeriesFormat;
}) {
  const setField = (key: string, value: number) => onChange({ ...config, [key]: value });
  const stageModes = config.modes as ModeConfig[] | undefined;
  const effectiveModes = stageModes ?? modes;
  const currentSeries = (config.seriesFormat as SeriesFormat | undefined) ?? defaultSeriesFormat;
  const setSeries = (s: SeriesFormat) => onChange({ ...config, seriesFormat: s });
  const standingsAllowed = STANDINGS_TYPE_STAGE_TYPES.includes(type);
  const currentStandingsType = (config.standingsType as StandingsType | undefined) ?? 'SCORE';
  const setStandingsType = (v: StandingsType) => onChange({ ...config, standingsType: v });
  const setStageModes = (next: ModeConfig[]) => onChange({ ...config, modes: next });
  const overrideModes = () => onChange({ ...config, modes: modes.map((m) => ({ ...m })) });
  const revertModes = () => {
    const nextConfig = { ...config };
    delete nextConfig.modes;
    onChange(nextConfig);
  };

  let typeFields: ReactNode;
  if (type === 'SEEDING') {
    typeFields = (
      <div className="flex items-center gap-2">
        <label className="text-[11px] text-ink-meta">Rounds</label>
        <input
          type="number" min={0} disabled={disabled}
          value={(config.rounds as number) ?? 5}
          onChange={e => setField('rounds', Number(e.target.value))}
          className="input w-20 text-xs disabled:cursor-not-allowed disabled:opacity-50"
        />
      </div>
    );
  } else if (type === 'SWISS') {
    typeFields = (
      <div className="flex flex-wrap items-center gap-x-3 gap-y-1.5">
        <div className="flex items-center gap-1.5">
          <label className="text-[11px] text-ink-meta">Advance at</label>
          <input
            type="number" min={1} disabled={disabled}
            value={(config.winThreshold as number) ?? 3}
            onChange={e => setField('winThreshold', Number(e.target.value))}
            className="input w-16 text-xs disabled:cursor-not-allowed disabled:opacity-50"
          />
          <span className="text-[11px] text-ink-meta">wins</span>
        </div>
        <div className="flex items-center gap-1.5">
          <label className="text-[11px] text-ink-meta">eliminate at</label>
          <input
            type="number" min={1} disabled={disabled}
            value={(config.lossThreshold as number) ?? 3}
            onChange={e => setField('lossThreshold', Number(e.target.value))}
            className="input w-16 text-xs disabled:cursor-not-allowed disabled:opacity-50"
          />
          <span className="text-[11px] text-ink-meta">losses</span>
        </div>
      </div>
    );
  } else if (type === 'DIVISIONS') {
    const applySuggestion = () => {
      if (numTeams === undefined) return;
      const plan = suggestGroupPlan(numTeams);
      onChange({ ...config, numGroups: plan.numGroups, advancePerGroup: plan.advancePerGroup });
    };
    typeFields = (
      <div className="space-y-1">
        <div className="flex flex-wrap items-center gap-x-3 gap-y-1.5">
          <div className="flex items-center gap-1.5">
            <label className="text-[11px] text-ink-meta">Divisions</label>
            <input
              type="number" min={1} disabled={disabled}
              value={(config.numGroups as number) ?? 2}
              onChange={e => setField('numGroups', Number(e.target.value))}
              className="input w-16 text-xs disabled:cursor-not-allowed disabled:opacity-50"
            />
          </div>
          <div className="flex items-center gap-1.5">
            <label className="text-[11px] text-ink-meta">Advance per group</label>
            <input
              type="number" min={1} disabled={disabled}
              value={(config.advancePerGroup as number) ?? 4}
              onChange={e => setField('advancePerGroup', Number(e.target.value))}
              className="input w-16 text-xs disabled:cursor-not-allowed disabled:opacity-50"
            />
          </div>
          <div className="flex items-center gap-1.5">
            <label className="text-[11px] text-ink-meta">Games per week</label>
            <input
              type="number" min={1} disabled={disabled}
              value={(config.gamesPerWeek as number) ?? 1}
              onChange={e => setField('gamesPerWeek', Number(e.target.value))}
              className="input w-16 text-xs disabled:cursor-not-allowed disabled:opacity-50"
            />
          </div>
          {!disabled && numTeams !== undefined && (
            <button type="button" onClick={applySuggestion} className="btn-ghost px-2 py-0.5 text-[11px]">
              Suggest
            </button>
          )}
        </div>
        <p className="text-[11px] text-ink-meta">
          e.g. 2 = each team plays 2 games per week — a soft guide the Matches page uses to group fixtures.
        </p>
        {!disabled && numTeams === undefined && (
          <p className="text-[11px] text-ink-meta">Suggestion available once teams enroll.</p>
        )}
      </div>
    );
  } else {
    // ELIMINATION_SINGLE / ELIMINATION_DOUBLE / ROUND_ROBIN — no real engine knobs.
    typeFields = <p className="text-xs text-ink-meta">{NO_CONFIG_COPY[type]}</p>;
  }

  return (
    <div className="mt-1.5 space-y-6">
      <div>
        <p className={GROUP_LABEL}>Stage settings</p>
        {typeFields}
      </div>
      {standingsAllowed && (
        <div className="border-t border-white/5 pt-5">
          <p className={GROUP_LABEL}>Standings</p>
          <select
            disabled={disabled}
            value={currentStandingsType}
            onChange={e => setStandingsType(e.target.value as StandingsType)}
            className="input w-36 text-xs disabled:cursor-not-allowed disabled:opacity-50"
          >
            <option value="SCORE">Score-based — default</option>
            <option value="WIN_LOSS">Win / Loss</option>
          </select>
        </div>
      )}
      {/* T5/UX-polish (Lisa's QA): per-stage game mode DEFINITIONS override,
          ONE click not two. Previously: expand a CollapsibleSection (click 1),
          then press "Override for this stage" (click 2). Now: a single
          always-visible row; "Edit for this stage" both seeds the stage's own
          copy of the comp's current modes AND reveals the editor in that same
          click (visibility is just `stageModes` existing — no separate
          expand/collapse state to manage). "Revert to competition modes"
          drops the override and collapses back to the summary row. */}
      <div className="border-t border-white/5 pt-5">
        <p className={GROUP_LABEL}>Game modes (this stage)</p>
        <fieldset disabled={disabled} className="min-w-0 space-y-2 disabled:opacity-50">
          {stageModes ? (
            <>
              <GameModesEditor modes={stageModes} onChange={setStageModes} />
              <button type="button" onClick={revertModes} className="text-[11px] text-ink-meta hover:text-ink-primary">
                Revert to competition modes
              </button>
            </>
          ) : (
            <div className="flex flex-col gap-1.5 sm:flex-row sm:items-center sm:justify-between sm:gap-2">
              <p className="text-[11px] text-ink-meta">Game modes: using the competition's modes</p>
              <button type="button" onClick={overrideModes} className="btn-ghost self-start px-2 py-0.5 text-[11px] sm:shrink-0 sm:self-auto">
                Edit for this stage
              </button>
            </div>
          )}
        </fieldset>
      </div>
      <div className="border-t border-white/5 pt-5">
        <p className={GROUP_LABEL}>Series format</p>
        <fieldset disabled={disabled} className="min-w-0 disabled:opacity-50">
          <SeriesFormatEditor modes={effectiveModes} series={currentSeries} onChange={setSeries} />
        </fieldset>
      </div>
    </div>
  );
}

export function StagePipelinePanel({ compId, modes, defaultSeriesFormat }: {
  compId: string;
  // Comp-level mode DEFINITIONS + the comp's current series format — the
  // latter seeds the DEFAULT every newly-added stage card starts from (the
  // Ruleset/series picker stays comp-level per the product spec's T3
  // reshape; see CompetitionCreateForm.tsx). Both ride down into
  // StageConfigFields via StagePipelineCard below.
  modes: ModeConfig[];
  defaultSeriesFormat: SeriesFormat;
}) {
  const qc = useQueryClient();
  const queryKey = ['compStages', compId];
  const { data: stages } = useQuery({
    queryKey,
    queryFn: () => api.listCompStages(compId),
    enabled: !!compId,
  });
  const [pickerOpen, setPickerOpen] = useState(false);
  const [error, setError] = useState('');

  const list = (stages ?? []).slice().sort((a, b) => a.ordinal - b.ordinal);
  const invalidate = () => qc.invalidateQueries({ queryKey });

  const addMut = useMutation({
    // New stages seed the comp's current series format as their own
    // starting point — an admin composing a pipeline of, say, SEEDING then
    // ROUND_ROBIN doesn't land on an empty Bo1 they have to notice and fill in.
    // Same default for standingsType — 'SCORE' matches the select's own
    // "default" option — but only for the types that carry the key at all
    // (STANDINGS_TYPE_STAGE_TYPES); ELIMINATION_SINGLE/ELIMINATION_DOUBLE
    // would 400 if it rode along (see shared/compStages.ts).
    mutationFn: (type: CompStageType) => api.addCompStage(compId, type, {
      seriesFormat: defaultSeriesFormat,
      ...(STANDINGS_TYPE_STAGE_TYPES.includes(type) ? { standingsType: 'SCORE' as const } : {}),
    }),
    onSuccess: () => { setError(''); setPickerOpen(false); invalidate(); },
    onError: (e: Error) => setError(parseApiError(e).message),
  });

  if (!compId) return null;

  // Nothing to show yet and the admin hasn't started adding one — just a
  // quiet entry point, not a whole empty Card.
  if (list.length === 0 && !pickerOpen) {
    return (
      <div className="mt-2">
        <button
          type="button"
          onClick={() => setPickerOpen(true)}
          className="btn-ghost inline-flex items-center gap-1.5 text-xs"
        >
          <Plus className="h-3.5 w-3.5" /> Add stage pipeline
        </button>
      </div>
    );
  }

  return (
    <Card className="mt-4">
      <div className="mb-3">
        <h3 className="text-sm font-semibold text-ink-primary">Stage Pipeline</h3>
        <p className="text-xs text-ink-meta">Ordered stages run one at a time — start requires every earlier stage to have ended.</p>
        <HelpText>
          <p className="mt-1 text-xs text-ink-meta">Lifecycle per stage: compose it, configure its settings while it's pending, Start locks those settings and generates its matches, End it to unlock the next stage's Start. Divisions and the elimination bracket work a little differently — Start proposes a split/seed order first; you review and adjust before Confirm actually generates fixtures. A bracket stage placed after a Divisions or a Swiss stage proposes its seed order straight from that stage's results — division final standings, or the Swiss survivors that reached IN.</p>
        </HelpText>
      </div>

      {list.length === 0 && (
        <p className="mb-3 text-xs text-ink-meta">No stages yet — pick a type below to start the pipeline.</p>
      )}

      {list.length > 0 && (
        // Connector line replaces the plain space-y-3 gap: a quiet 1px rail
        // (bg-white/[0.08]) under the ordinal badges ties consecutive stage
        // cards into one pipeline read, not a stack of unrelated blocks.
        // Its own h-3 supplies the same 12px rhythm space-y-3 used to.
        <div>
          {list.map((s, i) => (
            <div key={s.stageId}>
              <StagePipelineCard
                compId={compId}
                stage={s}
                isFirst={i === 0}
                isLast={i === list.length - 1}
                isOnly={list.length === 1}
                modes={modes}
                defaultSeriesFormat={defaultSeriesFormat}
                onChanged={invalidate}
              />
              {i < list.length - 1 && <div className="ml-[52px] h-3 w-px bg-white/[0.08]" />}
            </div>
          ))}
        </div>
      )}

      {/* "+ Add stage" — moved to the BOTTOM of the list (Lisa's QA: it used
          to sit top-right of the header, nowhere near where the new stage
          actually lands). The type picker opens in place of this row. */}
      <div className={cn(list.length > 0 && 'mt-3')}>
        {!pickerOpen ? (
          <button
            type="button"
            onClick={() => setPickerOpen(true)}
            className="flex w-full items-center gap-1.5 rounded-lg px-3 py-2 text-xs text-ink-meta transition hover:bg-white/[0.04] hover:text-ink-primary"
          >
            <Plus className="h-3.5 w-3.5" /> Add stage
          </button>
        ) : (
          <div className="rounded-lg bg-white/[0.04] p-3">
            <p className="mb-2 text-xs font-medium uppercase tracking-wide text-ink-meta">Choose a stage type</p>
            <div className="flex flex-wrap gap-1.5">
              {COMP_STAGE_TYPES.map((type) => (
                <button
                  key={type}
                  type="button"
                  disabled={addMut.isPending}
                  onClick={() => addMut.mutate(type)}
                  className="rounded-md bg-white/[0.06] px-2.5 py-1 text-xs text-ink-primary transition hover:bg-white/[0.1] disabled:opacity-50"
                >
                  {STAGE_TYPE_LABELS[type]}
                </button>
              ))}
            </div>
            <button
              type="button"
              onClick={() => { setPickerOpen(false); setError(''); }}
              className="mt-2 text-xs text-ink-meta hover:text-ink-primary"
            >
              Cancel
            </button>
          </div>
        )}
      </div>

      {error && <p className="mt-2 text-xs text-red-400">{error}</p>}
    </Card>
  );
}

// ── StagePipelineCard — one row, its own hooks (mutations) ─────────────────────
// Extracted so the list `.map()` above never puts hooks in a loop — each card
// owns its own update/remove/start/end mutations independently.

function StagePipelineCard({
  compId,
  stage,
  isFirst,
  isLast,
  isOnly,
  modes,
  defaultSeriesFormat,
  onChanged,
}: {
  compId: string;
  stage: CompStage;
  isFirst: boolean;
  isLast: boolean;
  isOnly: boolean;
  modes: ModeConfig[];
  defaultSeriesFormat: SeriesFormat;
  onChanged: () => void;
}) {
  const [error, setError] = useState('');
  // Expandable card (T3 — chevron, matching CollapsibleSection's house
  // idiom). Default COLLAPSED (Lisa's QA) so a multi-stage pipeline reads as
  // a scannable list, not a wall of settings — EXCEPT the stage that's
  // currently LIVE (the one the admin is actually watching right now) or the
  // pipeline's only stage (nothing to declutter with just one card), both of
  // which start open.
  const [expanded, setExpanded] = useState(stage.status === 'LIVE' || isOnly || !!stage.proposedGroups || !!stage.proposedEntrants);
  // R3/T5 (comp-lifecycle-pipeline, divisions/elimination gates): a proposal
  // appearing (the admin just clicked "Propose divisions"/"Propose bracket",
  // or another admin did) is exactly the moment this card needs the admin's
  // attention — force it open rather than leaving a freshly-proposed stage
  // collapsed.
  useEffect(() => {
    if (stage.proposedGroups || stage.proposedEntrants) setExpanded(true);
  }, [stage.proposedGroups, stage.proposedEntrants]);
  // Config draft — local until explicitly Saved (a network PATCH per
  // keystroke would be silly). Reset to the server value whenever a save
  // succeeds (configDirty flips back to false, so the field below reads
  // straight from `stage.config` again).
  const [configDraft, setConfigDraft] = useState<Record<string, unknown>>(stage.config);
  const [configDirty, setConfigDirty] = useState(false);

  // DIVISIONS' Suggest button needs the enrolled-clan count — same query key
  // Setup/TournamentSetup/ClanWorkbench etc. already share, so this rides the
  // existing cache instead of adding a redundant fetch. `enabled` keeps the
  // request from firing for every non-DIVISIONS card.
  const needsTeamsCount = stage.type === 'DIVISIONS';
  const { data: enrolledClans } = useQuery({
    queryKey: ['competitionClans', compId],
    queryFn: () => api.competitionClans(compId),
    enabled: needsTeamsCount,
  });

  // E4 (comp-lifecycle-pipeline, incomplete-Swiss warnings) — the End
  // button's confirm-dialog gate needs swissStatus.complete even when the
  // card is COLLAPSED (End lives in the always-visible header row below, not
  // inside `expanded`), so this query is independent of SwissRoundsPanel's
  // own — SAME queryKey though, so React Query dedupes/shares the one
  // request when both are mounted (card expanded + LIVE).
  const needsSwissCompletion = stage.type === 'SWISS' && stage.status === 'LIVE';
  const { data: swissScoreboard } = useQuery({
    queryKey: ['swissScoreboard', compId, stage.stageId],
    queryFn: () => api.getSwissScoreboard(compId, stage.stageId),
    enabled: needsSwissCompletion,
  });
  const swissEndRisk = swissEndRiskMessage(stage, swissScoreboard?.summary);
  const [endConfirmOpen, setEndConfirmOpen] = useState(false);

  const reorderMut = useMutation({
    mutationFn: (toOrdinal: number) => api.updateCompStage(compId, stage.stageId, { ordinal: toOrdinal }),
    onSuccess: () => { setError(''); onChanged(); },
    onError: (e: Error) => setError(parseApiError(e).message),
  });
  const removeMut = useMutation({
    mutationFn: () => api.removeCompStage(compId, stage.stageId),
    onSuccess: () => { setError(''); onChanged(); },
    onError: (e: Error) => setError(parseApiError(e).message),
  });
  const startMut = useMutation({
    mutationFn: () => api.startCompStage(compId, stage.stageId),
    onSuccess: () => { setError(''); onChanged(); },
    onError: (e: Error) => setError(parseApiError(e).message),
  });
  const endMut = useMutation({
    mutationFn: () => api.endCompStage(compId, stage.stageId),
    onSuccess: () => { setError(''); onChanged(); },
    onError: (e: Error) => setError(parseApiError(e).message),
  });
  const configMut = useMutation({
    mutationFn: (config: Record<string, unknown>) => api.updateCompStage(compId, stage.stageId, { config }),
    onSuccess: () => { setError(''); setConfigDirty(false); onChanged(); },
    onError: (e: Error) => setError(parseApiError(e).message),
  });

  // P2-c (guided-wizard Phase 2, Lisa 2026-07-16) — optional ESTIMATED
  // start/end for this stage. Display-only: gates nothing (see CompStage's
  // doc comment in lib/api.ts), never auto-starts/ends a stage. Editable
  // while PENDING or LIVE (a running stage's estimated end is exactly what
  // needs refining mid-flight) — deliberately LOOSER than `isEditable` below
  // (PENDING-only, structural config), locked only once ENDED. Same
  // draft/dirty/Save/Cancel idiom as configDraft above — local until
  // explicitly saved, then falls back to reading straight off `stage` again.
  const [plannedStartDraft, setPlannedStartDraft] = useState(
    stage.plannedStartAt != null ? epochToLocalDateTimeInputValue(stage.plannedStartAt) : '',
  );
  const [plannedEndDraft, setPlannedEndDraft] = useState(
    stage.plannedEndAt != null ? epochToLocalDateTimeInputValue(stage.plannedEndAt) : '',
  );
  const [plannedDirty, setPlannedDirty] = useState(false);
  const shownPlannedStart = plannedDirty ? plannedStartDraft : (stage.plannedStartAt != null ? epochToLocalDateTimeInputValue(stage.plannedStartAt) : '');
  const shownPlannedEnd = plannedDirty ? plannedEndDraft : (stage.plannedEndAt != null ? epochToLocalDateTimeInputValue(stage.plannedEndAt) : '');
  const plannedInverted = !!shownPlannedStart && !!shownPlannedEnd
    && new Date(shownPlannedStart).getTime() > new Date(shownPlannedEnd).getTime();
  const plannedDatesEditable = stage.status !== 'ENDED';
  const plannedMut = useMutation({
    mutationFn: () => api.updateCompStage(compId, stage.stageId, {
      plannedStartAt: plannedStartDraft ? new Date(plannedStartDraft).getTime() : null,
      plannedEndAt: plannedEndDraft ? new Date(plannedEndDraft).getTime() : null,
    }),
    onSuccess: () => { setError(''); setPlannedDirty(false); onChanged(); },
    onError: (e: Error) => setError(parseApiError(e).message),
  });

  const isPending = reorderMut.isPending || removeMut.isPending || startMut.isPending || endMut.isPending;
  const isEditable = stage.status === 'PENDING';

  // Ordinal badge lights up (accent ink + slightly raised accent wash) only
  // when this card is the one the admin is actually looking at — expanded,
  // or LIVE right now — otherwise it stays a neutral squared chip. Orange
  // stays an ACCENT (house doctrine): the spine + badge glow are the only
  // two places it shows up on a collapsed pipeline.
  const badgeActive = expanded || stage.status === 'LIVE';

  return (
    // Distinct per-stage card (Lisa's QA): elevated bg-white/[0.07] slab (one
    // step above the section's Card bg) + the house shadow idiom + real
    // padding so each stage reads as its own solid block, not one grey
    // run-on slab — clear vertical spacing (+ a quiet connector line) between
    // cards comes from the list above. No box border (house doctrine); the
    // 2px accent edge below is a pointer to the ACTIVE card, not a border.
    <div className={cn('rounded-xl bg-white/[0.07] p-4 shadow-elev1', expanded && 'border-l-2 border-accent-500')}>
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0 flex-1">
          <button
            type="button"
            onClick={() => setExpanded(e => !e)}
            className="flex w-full min-w-0 items-center gap-2 text-left"
          >
            {expanded
              ? <ChevronDown className="h-3.5 w-3.5 shrink-0 text-ink-meta" />
              : <ChevronRight className="h-3.5 w-3.5 shrink-0 text-ink-meta" />}
            <span className={cn(
              'flex h-7 w-7 shrink-0 items-center justify-center rounded-md font-display text-[11px] font-black',
              badgeActive ? 'bg-accent-500/10 text-accent-500' : 'bg-white/[0.08] text-ink-secondary'
            )}>
              {stage.ordinal + 1}
            </span>
            <p className="text-sm font-medium text-ink-primary">{STAGE_TYPE_LABELS[stage.type] ?? stage.type}</p>
            <StatusBadge status={stage.status} />
            {!isEditable && <Lock className="h-3 w-3 text-ink-meta" />}
            {/* P2-c: quiet glanceable estimate — visible even collapsed
                (most cards default collapsed — see `expanded`'s initializer
                above), so a participant doesn't have to expand every
                stage just to see the rough schedule. Labelled "Est." so it's
                never mistaken for a locked/confirmed date. */}
            {(stage.plannedStartAt != null || stage.plannedEndAt != null) && (
              <span className="shrink-0 font-mono text-[10px] text-ink-meta">
                Est. {stage.plannedStartAt != null ? new Date(stage.plannedStartAt).toLocaleDateString() : '—'}
                {'–'}
                {stage.plannedEndAt != null ? new Date(stage.plannedEndAt).toLocaleDateString() : '—'}
              </span>
            )}
          </button>
          {expanded && (
            // Body sits inside the collapsible, separated from the header
            // row by a real gap (mt-3) — same header/body breathing room
            // CollapsibleSection gives its own children (its mt-4).
            <div className="mt-3">
              {isEditable && (
                <HelpText>
                  <p className="mb-2 text-[11px] text-ink-meta">Settings lock the moment this stage starts — edit them now while it's still pending.</p>
                </HelpText>
              )}
              <StageConfigFields
                type={stage.type}
                config={configDirty ? configDraft : stage.config}
                disabled={!isEditable}
                numTeams={needsTeamsCount ? enrolledClans?.length : undefined}
                modes={modes}
                defaultSeriesFormat={defaultSeriesFormat}
                onChange={(next) => { setConfigDraft(next); setConfigDirty(true); }}
              />
              {isEditable && configDirty && (
                <div className="mt-1.5 flex items-center gap-2">
                  <button
                    type="button"
                    disabled={configMut.isPending}
                    onClick={() => configMut.mutate(configDraft)}
                    className="btn-primary px-2 py-0.5 text-[11px] disabled:opacity-50"
                  >
                    {configMut.isPending ? 'Saving…' : 'Save'}
                  </button>
                  <button
                    type="button"
                    onClick={() => { setConfigDraft(stage.config); setConfigDirty(false); }}
                    className="text-[11px] text-ink-meta hover:text-ink-primary"
                  >
                    Cancel
                  </button>
                </div>
              )}
              {!isEditable && (
                <p className="mt-1 text-[11px] text-ink-meta">Locked — settings can only change while this stage is PENDING.</p>
              )}
              {/* P2-c: optional ESTIMATED schedule — display-only, gates
                  nothing (see the doc comment on `plannedDatesEditable`
                  above). Inputs while PENDING/LIVE; a plain labelled readout
                  once ENDED (or whenever neither date is set at all — unset
                  means "no estimate shown", not an empty pair of fields). */}
              <div className="border-t border-white/5 pt-5">
                <p className={GROUP_LABEL}>Estimated schedule</p>
                {plannedDatesEditable ? (
                  <>
                    <div className="flex flex-wrap items-center gap-x-3 gap-y-1.5">
                      <div className="flex items-center gap-1.5">
                        <label className="text-[11px] text-ink-meta">Est. start</label>
                        <input
                          type="datetime-local"
                          value={shownPlannedStart}
                          onChange={e => { setPlannedStartDraft(e.target.value); setPlannedDirty(true); }}
                          className="input w-52 text-xs [color-scheme:dark]"
                        />
                      </div>
                      <div className="flex items-center gap-1.5">
                        <label className="text-[11px] text-ink-meta">Est. end</label>
                        <input
                          type="datetime-local"
                          value={shownPlannedEnd}
                          onChange={e => { setPlannedEndDraft(e.target.value); setPlannedDirty(true); }}
                          className="input w-52 text-xs [color-scheme:dark]"
                        />
                      </div>
                    </div>
                    <p className="mt-1 text-[11px] text-ink-meta">
                      Optional — a rough schedule shown to teams. Never locks or auto-starts/ends
                      this stage; progression stays manual (Start/End above).
                    </p>
                    {plannedInverted && (
                      <p className="mt-1 text-xs text-red-400">Estimated start must be at or before estimated end.</p>
                    )}
                    {plannedDirty && (
                      <div className="mt-1.5 flex items-center gap-2">
                        <button
                          type="button"
                          disabled={plannedMut.isPending || plannedInverted}
                          onClick={() => plannedMut.mutate()}
                          className="btn-primary px-2 py-0.5 text-[11px] disabled:opacity-50"
                        >
                          {plannedMut.isPending ? 'Saving…' : 'Save'}
                        </button>
                        <button
                          type="button"
                          onClick={() => {
                            setPlannedStartDraft(stage.plannedStartAt != null ? epochToLocalDateTimeInputValue(stage.plannedStartAt) : '');
                            setPlannedEndDraft(stage.plannedEndAt != null ? epochToLocalDateTimeInputValue(stage.plannedEndAt) : '');
                            setPlannedDirty(false);
                          }}
                          className="text-[11px] text-ink-meta hover:text-ink-primary"
                        >
                          Cancel
                        </button>
                      </div>
                    )}
                  </>
                ) : (stage.plannedStartAt != null || stage.plannedEndAt != null) ? (
                  <p className="text-xs text-ink-secondary">
                    Estimated {stage.plannedStartAt != null ? new Date(stage.plannedStartAt).toLocaleDateString() : '—'}
                    {' – '}
                    {stage.plannedEndAt != null ? new Date(stage.plannedEndAt).toLocaleDateString() : '—'}
                  </p>
                ) : (
                  <p className="text-[11px] text-ink-meta">No estimate was set.</p>
                )}
              </div>
              {/* R2 (comp-lifecycle-pipeline): once the stage is LIVE with
                  config.rounds >= 1, the rounds themselves — round tabs,
                  match results, the derived scoreboard, "Generate round
                  N+1", and the apply-standings recommendation. Renders ABOVE
                  the static seeding table below (rounds are seeded FROM that
                  table, and "Apply standings" writes back ONTO it). No-ops
                  (renders null) for a legacy SEEDING stage with no rounds
                  configured — see SeedingRoundsPanel's own early return. */}
              {stage.type === 'SEEDING' && stage.status === 'LIVE' && (
                <SeedingRoundsPanel compId={compId} stage={stage} />
              )}
              {/* SEEDING's runtime (src/shared/seedingTable.ts) — the ordered
                  table of enrolled teams + its audit log. Renders while the
                  stage is PENDING or LIVE; hidden once ENDED (the table is
                  frozen at that point — nothing left to move). */}
              {stage.type === 'SEEDING' && (stage.status === 'PENDING' || stage.status === 'LIVE') && (
                <SeedingTablePanel compId={compId} />
              )}
              {/* R3 (comp-lifecycle-pipeline): the divisions gate. Once
                  /start has proposed a snake split, the card shows it as
                  editable division columns instead of a bare "Start"
                  button — review, move any team, Confirm to generate. */}
              {stage.type === 'DIVISIONS' && stage.status === 'PENDING' && stage.proposedGroups && (
                <DivisionsProposalPanel
                  compId={compId}
                  stageId={stage.stageId}
                  proposedGroups={stage.proposedGroups}
                  onChanged={onChanged}
                />
              )}
              {/* R4 (comp-lifecycle-pipeline, division scoreboards): once
                  confirm-divisions has flipped the stage LIVE, each division
                  gets its own standings + rounds card here — see
                  DivisionsScoreboardPanel's own header comment. */}
              {stage.type === 'DIVISIONS' && stage.status === 'LIVE' && (
                <DivisionsScoreboardPanel compId={compId} stage={stage} />
              )}
              {/* E1 (comp-lifecycle-pipeline, Swiss rounds panel): the
                  steering wheel for a LIVE SWISS stage — standings,
                  round-by-round result entry, Complete round / Pair next
                  round. Round 1 is already generated at Start (unchanged);
                  see SwissRoundsPanel's own header comment for why rounds 2+
                  drive the legacy /comps/:compId/swiss/* routes. */}
              {stage.type === 'SWISS' && stage.status === 'LIVE' && (
                <SwissRoundsPanel compId={compId} stage={stage} />
              )}
              {/* T5 (comp-lifecycle-pipeline, elimination gate): the SAME
                  propose -> review -> confirm gate as DIVISIONS above, over an
                  ordered entrant list instead of a group split. Once /start
                  has proposed the bracket order, the card shows it as a
                  reorderable seed list instead of a bare "Start" button. */}
              {isElimStage(stage.type) && stage.status === 'PENDING' && stage.proposedEntrants && (
                <EliminationProposalPanel
                  compId={compId}
                  stageId={stage.stageId}
                  proposedEntrants={stage.proposedEntrants}
                  proposedEntrantsOrigins={stage.proposedEntrantsOrigins}
                  proposedEntrantsWarning={stage.proposedEntrantsWarning}
                  onChanged={onChanged}
                />
              )}
              {/* Winner surfacing: once confirm-bracket has flipped the stage
                  LIVE, a quiet read-only bracket-progress/champion line — see
                  EliminationBracketPanel's own header comment for what it
                  does and doesn't own. */}
              {isElimStage(stage.type) && stage.status === 'LIVE' && (
                <EliminationBracketPanel compId={compId} stage={stage} />
              )}
            </div>
          )}
        </div>

        <div className="flex shrink-0 items-center gap-1">
          {isEditable && (
            <>
              <button
                type="button"
                title="Move up"
                disabled={isFirst || isPending}
                onClick={() => reorderMut.mutate(stage.ordinal - 1)}
                className="rounded p-1 text-ink-meta transition hover:bg-white/[0.06] hover:text-ink-primary disabled:opacity-30"
              >
                <ChevronUp className="h-3.5 w-3.5" />
              </button>
              <button
                type="button"
                title="Move down"
                disabled={isLast || isPending}
                onClick={() => reorderMut.mutate(stage.ordinal + 1)}
                className="rounded p-1 text-ink-meta transition hover:bg-white/[0.06] hover:text-ink-primary disabled:opacity-30"
              >
                <ChevronDown className="h-3.5 w-3.5" />
              </button>
              <button
                type="button"
                title="Remove stage"
                disabled={isPending}
                onClick={() => removeMut.mutate()}
                className="rounded p-1 text-ink-meta transition hover:bg-white/[0.06] hover:text-red-400 disabled:opacity-30"
              >
                <X className="h-3.5 w-3.5" />
              </button>
            </>
          )}
          {/* R3/T5 (comp-lifecycle-pipeline): a DIVISIONS/ELIMINATION stage
              with an active proposal has NO Start button here at all — its
              Confirm/Re-propose buttons live inside DivisionsProposalPanel/
              EliminationProposalPanel below instead. Before a proposal
              exists, the SAME /start call is relabeled "Propose divisions"/
              "Propose bracket" (it proposes, not generates). */}
          {stage.status === 'PENDING'
            && !(stage.type === 'DIVISIONS' && stage.proposedGroups)
            && !(isElimStage(stage.type) && stage.proposedEntrants) && (
            <button
              type="button"
              disabled={isPending}
              onClick={() => startMut.mutate()}
              className="btn-primary inline-flex items-center gap-1 px-2 py-1 text-xs disabled:opacity-50"
            >
              <PlayCircle className="h-3.5 w-3.5" />
              {' '}
              {stage.type === 'DIVISIONS' ? 'Propose divisions' : isElimStage(stage.type) ? 'Propose bracket' : 'Start'}
            </button>
          )}
          {/* E4 (comp-lifecycle-pipeline, incomplete-Swiss warnings): a LIVE
              SWISS stage whose swissStatus.complete is false gets a confirm
              step instead of ending immediately — canEnd itself stays
              permissive (warn, don't block), this is the warn half. Every
              other stage/status keeps the single-click End it always had. */}
          {stage.status === 'LIVE' && !endConfirmOpen && (
            <button
              type="button"
              disabled={isPending}
              onClick={() => (swissEndRisk ? setEndConfirmOpen(true) : endMut.mutate())}
              className="btn-ghost inline-flex items-center gap-1 px-2 py-1 text-xs disabled:opacity-50"
            >
              <Square className="h-3.5 w-3.5" /> End
            </button>
          )}
          {stage.status === 'LIVE' && endConfirmOpen && (
            <div className="flex items-center gap-2">
              <span className="text-[11px] text-amber-300">{swissEndRisk}</span>
              <button
                type="button"
                disabled={isPending}
                onClick={() => { setEndConfirmOpen(false); endMut.mutate(); }}
                className="btn-primary px-2 py-0.5 text-[11px] disabled:opacity-50"
              >
                Yes, end it
              </button>
              <button
                type="button"
                onClick={() => setEndConfirmOpen(false)}
                className="text-[11px] text-ink-meta hover:text-ink-primary"
              >
                Cancel
              </button>
            </div>
          )}
        </div>
      </div>
      {error && <p className="mt-1.5 text-xs text-red-400">{error}</p>}
    </div>
  );
}

// ── DivisionsProposalPanel — R3 (comp-lifecycle-pipeline), the divisions gate ──
// Renders the auto-snake proposal (POST .../start on a DIVISIONS stage) as
// editable division columns: Lisa's original flow was "RECOMMENDED
// placements shown; admin can MOVE a team between divisions
// manually" — a <select> per team (not up/down arrows, SeedingTablePanel's
// idiom) is the cleaner fit here because a division is a CATEGORY, not a
// linear position; picking "Division 3" directly beats nudging one step at a
// time through N divisions. Group sizes may end up UNEVEN once teams are
// moved (Q2) — the only client-side guard is the same floor the server
// enforces (every division needs >=2 teams to play a fixture), surfaced as
// live copy so an admin sees the problem before hitting Confirm, not after.
//
// Local `draft` state holds the WORKING copy the admin is editing; it only
// ever gets pushed to the server on Confirm. Re-propose calls the SAME
// /start endpoint again (server-side this recomputes the snake from current
// enrollment/seeding-table order — see proposeDivisionsGroups's doc comment
// in portalApi/index.ts) — `onChanged` invalidates the stages query, which
// brings back a fresh `proposedGroups` and the effect below resets the draft
// to it.
function DivisionsProposalPanel({
  compId,
  stageId,
  proposedGroups,
  onChanged,
}: {
  compId: string;
  stageId: string;
  proposedGroups: string[][];
  onChanged: () => void;
}) {
  const [draft, setDraft] = useState<string[][]>(proposedGroups);
  const [error, setError] = useState('');

  // Reset the working draft whenever the SERVER's proposal changes (a fresh
  // propose or re-propose) — same "sync to server truth" idiom
  // SeedingTableRowItem's own draft-reset effect uses.
  useEffect(() => {
    setDraft(proposedGroups);
  }, [proposedGroups]);

  const moveTeam = (clanId: string, fromIdx: number, toIdx: number) => {
    if (fromIdx === toIdx) return;
    setDraft((cur) => cur.map((g, i) => {
      if (i === fromIdx) return g.filter((id) => id !== clanId);
      if (i === toIdx) return [...g, clanId];
      return g;
    }));
  };

  // Live validation copy — the SAME floor the server's
  // initGroupStageFromConfirmedGroups enforces (GROUP_TOO_SMALL), surfaced
  // client-side before Confirm is even attempted.
  const tooSmall = draft.reduce<number[]>((acc, g, i) => (g.length < 2 ? [...acc, i] : acc), []);

  const confirmMut = useMutation({
    mutationFn: () => api.confirmDivisions(compId, stageId, draft),
    onSuccess: () => { setError(''); onChanged(); },
    onError: (e: Error) => setError(parseApiError(e).message),
  });
  const reproposeMut = useMutation({
    mutationFn: () => api.startCompStage(compId, stageId),
    onSuccess: () => { setError(''); onChanged(); },
    onError: (e: Error) => setError(parseApiError(e).message),
  });
  const busy = confirmMut.isPending || reproposeMut.isPending;

  return (
    <div className="mt-3 border-t border-white/5 pt-5">
      <p className="mb-2 font-display text-[10px] font-black uppercase tracking-wider text-ink-meta">Proposed Divisions</p>
      <HelpText>
        <p className="mb-2 text-[11px] text-ink-meta">This is a recommendation, not final — move any team into a different division below, then Confirm to lock it in and generate fixtures. Re-propose throws the edits away and recomputes the suggestion from scratch.</p>
      </HelpText>

      <div className="grid gap-2 sm:grid-cols-2">
        {draft.map((group, gi) => (
          <div key={gi} className="rounded-lg bg-white/[0.04] p-2.5">
            <p className="mb-1.5 text-xs font-medium text-ink-primary">
              Division {gi + 1} <span className="text-ink-meta">({group.length})</span>
            </p>
            {group.length === 0 && (
              <p className="text-[11px] text-ink-meta">No teams in this division.</p>
            )}
            <div className="space-y-1">
              {group.map((clanId) => (
                <div key={clanId} className="flex items-center justify-between gap-2 rounded bg-white/[0.04] px-2 py-1">
                  <span className="min-w-0 truncate text-xs text-ink-primary">{displayClanName(clanId)}</span>
                  <select
                    value={gi}
                    disabled={busy}
                    onChange={(e) => moveTeam(clanId, gi, Number(e.target.value))}
                    className="input w-28 shrink-0 text-[11px] disabled:cursor-not-allowed disabled:opacity-50"
                  >
                    {draft.map((_, oi) => (
                      <option key={oi} value={oi}>Division {oi + 1}</option>
                    ))}
                  </select>
                </div>
              ))}
            </div>
          </div>
        ))}
      </div>

      {tooSmall.length > 0 && (
        <p className="mt-2 text-[11px] text-red-400">
          Division{tooSmall.length > 1 ? 's' : ''} {tooSmall.map((i) => i + 1).join(', ')} need{tooSmall.length === 1 ? 's' : ''} at least 2 teams to play a fixture.
        </p>
      )}

      <div className="mt-2.5 flex items-center gap-2">
        <button
          type="button"
          disabled={busy || tooSmall.length > 0}
          onClick={() => confirmMut.mutate()}
          className="btn-primary px-2.5 py-1 text-xs disabled:opacity-50"
        >
          {confirmMut.isPending ? 'Confirming…' : 'Confirm divisions & generate fixtures'}
        </button>
        <button
          type="button"
          disabled={busy}
          onClick={() => reproposeMut.mutate()}
          className="btn-ghost px-2.5 py-1 text-xs disabled:opacity-50"
        >
          {reproposeMut.isPending ? 'Re-proposing…' : 'Re-propose'}
        </button>
      </div>
      {error && <p className="mt-1.5 text-xs text-red-400">{error}</p>}
    </div>
  );
}

// ── EliminationProposalPanel — T5 (comp-lifecycle-pipeline), the elimination
// gate ───────────────────────────────────────────────────────────────────────
// Renders the proposed bracket order (POST .../start on an ELIMINATION_SINGLE/
// ELIMINATION_DOUBLE stage) as a reorderable seed list: unlike DIVISIONS'
// proposal (a CATEGORY per team), this is a LINEAR POSITION — position =
// seed — so up/down arrows + type-a-seed (SeedingTablePanel's idiom) fit
// better than a <select>. Each row also shows its provenance tag (e.g.
// "D1 #1", "Seed #3", "Entry #5" — server-computed, see CompStage's doc
// comment) so the admin can see WHY a team landed where it did before
// deciding whether to move it.
//
// Local `draft` state holds the WORKING copy the admin is editing; it only
// ever gets pushed to the server on Confirm — reordering here is a pure
// client-side array move, no network round trip per nudge (unlike
// SeedingTablePanel's live table, which persists every move immediately —
// this is still a DRAFT proposal, nothing generated yet). Re-propose calls
// the SAME /start endpoint again (server-side this re-derives from current
// division-scoreboard/seeding-table/enrollment state — see
// proposeEliminationOrder's doc comment in portalApi/index.ts) — `onChanged`
// invalidates the stages query, which brings back a fresh `proposedEntrants`
// and the effect below resets the draft to it.
//
// The round-1 preview below is computed CLIENT-SIDE from the current draft
// order via bracketPreview.ts's previewRound1 (a tiny replica of
// shared/bracket.ts's seedingOrder/nextPowerOfTwo — see that file's header
// for why this is a replica, not an import) — purely a preview; Confirm is
// what actually asks the server to generate the bracket.
function EliminationProposalPanel({
  compId,
  stageId,
  proposedEntrants,
  proposedEntrantsOrigins,
  proposedEntrantsWarning,
  onChanged,
}: {
  compId: string;
  stageId: string;
  proposedEntrants: string[];
  proposedEntrantsOrigins?: string[];
  // E2 (comp-lifecycle-pipeline, Swiss->bracket seeding): non-blocking heads-
  // up from the server (a prior Swiss stage wasn't cleanly sourced from —
  // not yet ended, or ended before every team resolved to IN/OUT). Surfaced
  // loud, never a hard block (see the banner below) — canEnd stays permissive.
  proposedEntrantsWarning?: string;
  onChanged: () => void;
}) {
  const [draft, setDraft] = useState<string[]>(proposedEntrants);
  const [error, setError] = useState('');

  // Reset the working draft whenever the SERVER's proposal changes (a fresh
  // propose or re-propose) — same "sync to server truth" idiom
  // DivisionsProposalPanel's own draft-reset effect uses.
  useEffect(() => {
    setDraft(proposedEntrants);
  }, [proposedEntrants]);

  // Provenance is keyed by the ORIGINAL proposal order (parallel arrays), not
  // the current draft order — a team's "why here" origin describes where the
  // server's proposal put it, which stays meaningful even after the admin
  // moves it.
  const originByName = new Map(proposedEntrants.map((name, i) => [name, proposedEntrantsOrigins?.[i]]));

  const moveTo = (fromIdx: number, toSeed: number) => {
    const toIdx = Math.max(0, Math.min(toSeed - 1, draft.length - 1));
    if (fromIdx === toIdx) return;
    setDraft((cur) => {
      const next = cur.slice();
      const [moved] = next.splice(fromIdx, 1);
      next.splice(toIdx, 0, moved);
      return next;
    });
  };

  const pairings = previewRound1(draft);

  const confirmMut = useMutation({
    mutationFn: () => api.confirmBracket(compId, stageId, draft),
    onSuccess: () => { setError(''); onChanged(); },
    onError: (e: Error) => setError(parseApiError(e).message),
  });
  const reproposeMut = useMutation({
    mutationFn: () => api.startCompStage(compId, stageId),
    onSuccess: () => { setError(''); onChanged(); },
    onError: (e: Error) => setError(parseApiError(e).message),
  });
  const busy = confirmMut.isPending || reproposeMut.isPending;

  return (
    <div className="mt-3 border-t border-white/5 pt-5">
      <p className="mb-2 font-display text-[10px] font-black uppercase tracking-wider text-ink-meta">Proposed Bracket Order</p>
      <HelpText>
        <p className="mb-2 text-[11px] text-ink-meta">
          This is a recommendation, not final — the tag on each team shows why it's seeded where it is
          (its division finish, seeding-table position, or entry order). Nudge with the arrows or type a new
          seed, then Confirm to lock it in and generate the bracket. Re-propose throws the edits away and
          recomputes the suggestion from scratch.
        </p>
      </HelpText>

      {/* E2 (comp-lifecycle-pipeline, Swiss->bracket seeding): loud,
          non-blocking heads-up — a prior Swiss stage wasn't cleanly sourced
          from. Typography-only (house doctrine: no colour-strip banners) —
          amber text is the SAME soft-caution language StatusBadge already
          uses for PENDING, distinct from a hard red error. */}
      {proposedEntrantsWarning && (
        <p className="mb-2 text-[11px] text-amber-300">{proposedEntrantsWarning}</p>
      )}

      <div className="space-y-1">
        {draft.map((clanId, i) => (
          <EliminationSeedRow
            key={clanId}
            seed={i + 1}
            clanId={clanId}
            origin={originByName.get(clanId)}
            isFirst={i === 0}
            isLast={i === draft.length - 1}
            disabled={busy}
            onMoveTo={(toSeed) => moveTo(i, toSeed)}
          />
        ))}
      </div>

      {pairings.length > 0 && (
        <div className="mt-2.5">
          <p className="mb-1 text-[11px] font-medium uppercase tracking-wide text-ink-meta">Round 1 preview</p>
          <div className="space-y-1">
            {pairings.map((p, i) => (
              <p key={i} className="text-[11px] text-ink-secondary">
                {p.teamA ? displayClanName(p.teamA) : 'Bye'} vs {p.teamB ? displayClanName(p.teamB) : 'Bye'}
              </p>
            ))}
          </div>
        </div>
      )}

      <div className="mt-2.5 flex items-center gap-2">
        <button
          type="button"
          disabled={busy}
          onClick={() => confirmMut.mutate()}
          className="btn-primary px-2.5 py-1 text-xs disabled:opacity-50"
        >
          {confirmMut.isPending ? 'Confirming…' : 'Confirm bracket & generate'}
        </button>
        <button
          type="button"
          disabled={busy}
          onClick={() => reproposeMut.mutate()}
          className="btn-ghost px-2.5 py-1 text-xs disabled:opacity-50"
        >
          {reproposeMut.isPending ? 'Re-proposing…' : 'Re-propose'}
        </button>
      </div>
      {error && <p className="mt-1.5 text-xs text-red-400">{error}</p>}
    </div>
  );
}

// ── EliminationSeedRow — one row, its own "type a number" draft state ──────
// Extracted so the list `.map()` above never puts hooks in a loop — same
// idiom SeedingTableRowItem uses for the seeding table's own rows. Reordering
// here is purely local (onMoveTo tells the PARENT to splice its draft array —
// no per-row network call, unlike SeedingTableRowItem's live moveMut), since
// nothing is persisted until Confirm.
function EliminationSeedRow({
  seed,
  clanId,
  origin,
  isFirst,
  isLast,
  disabled,
  onMoveTo,
}: {
  seed: number;
  clanId: string;
  origin?: string;
  isFirst: boolean;
  isLast: boolean;
  disabled: boolean;
  onMoveTo: (toSeed: number) => void;
}) {
  const [draftSeed, setDraftSeed] = useState(String(seed));

  useEffect(() => {
    setDraftSeed(String(seed));
  }, [seed]);

  const commitDraft = () => {
    const parsed = Number(draftSeed);
    if (!Number.isInteger(parsed) || parsed < 1 || parsed === seed) {
      setDraftSeed(String(seed));
      return;
    }
    onMoveTo(parsed);
  };

  return (
    <div className="flex items-center gap-2 rounded-lg bg-white/[0.04] px-2.5 py-1.5">
      <span className="w-6 shrink-0 text-right text-xs font-medium text-ink-meta">{seed}.</span>
      <span className="min-w-0 flex-1 truncate text-sm text-ink-primary">{displayClanName(clanId)}</span>
      {origin && <span className="shrink-0 text-[10px] text-ink-meta">{origin}</span>}
      <input
        type="number"
        min={1}
        value={draftSeed}
        disabled={disabled}
        onChange={(e) => setDraftSeed(e.target.value)}
        onBlur={commitDraft}
        onKeyDown={(e) => { if (e.key === 'Enter') (e.target as HTMLInputElement).blur(); }}
        className="input w-12 shrink-0 text-center text-xs disabled:cursor-not-allowed disabled:opacity-50"
      />
      <button
        type="button"
        title="Move up"
        disabled={isFirst || disabled}
        onClick={() => onMoveTo(seed - 1)}
        className="rounded p-1 text-ink-meta transition hover:bg-white/[0.06] hover:text-ink-primary disabled:opacity-30"
      >
        <ChevronUp className="h-3.5 w-3.5" />
      </button>
      <button
        type="button"
        title="Move down"
        disabled={isLast || disabled}
        onClick={() => onMoveTo(seed + 1)}
        className="rounded p-1 text-ink-meta transition hover:bg-white/[0.06] hover:text-ink-primary disabled:opacity-30"
      >
        <ChevronDown className="h-3.5 w-3.5" />
      </button>
    </div>
  );
}
